import React, { useEffect, useState } from 'react';
import Filter from '../domain/filter';
import Machine from '../domain/machine';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Grid,
    IconButton,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import FilterProductSelectAutoComplete from './filterproductselectdialog';
import FilterProductChangeDialog from './filterproductchangedialog';
import { CalculateMaterialSpeed } from '../common/machinematerialspeed';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import NumericInputElement, { TypeOnChange } from '../common/numericinput';
import DeleteIcon from '@mui/icons-material/Delete';
import { formatCurrency, parseDecimal, currencySymbolFor } from '../common/common';
import ConfirmationDialog from '../common/confirmationdialog';
import WarningIcon from '@mui/icons-material/Warning';
import { getProductPricingData, getBundlePricingData } from '../modules/pricingcachedata';
import FilterMargin from './filtermargin';
import FilterProductWarning from './filterproductwarning';
import ObjectIdGenerator from '../modules/objectidgenerator';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Add from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import SurveyFilterInfo from './surveyfilterinfo';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const styles = {
    machineName: {
        marginLeft: '20px',
    },
    table: {
        width: '100%',
    },
    swapIconCol: {
        textAlign: 'center',
    },
    warning: {
        color: 'orange',
        verticalAlign: 'middle',
        display: 'inline-flex',
        fontSize: '0.9em',
    },
    bundleproduct: {
        fontSize: '0.75em',
    },
    containsChangesFromSurvey: {
        textDecoration: 'line-through',
    },
    highlight: {
        fontSize: '1.1em',
        margin: '1em',
        padding: '1em',
        borderWidth: 1,
        borderStyle: 'dashed',
        borderRadius: 4,
        borderColor: '#1976d2',
    },
};

const filterMatchesFilterInfo = (filter, filterInfo) => {
    if (!filter || !filter.product || !filterInfo) {
        return false;
    }
    if (
        (!filterInfo.filterType || filterInfo.filterType === '') &&
        filter.product.dimensionHeightMm === filterInfo.dimensionHeight &&
        filter.product.dimensionWidthMm === filterInfo.dimensionWidth &&
        filter.product.dimensionDepthMm <= filterInfo.maxDepth
    ) {
        return true;
    }
    const efficiency = filter.product.classP ? parseFloat(filter.product.classP.replace('%', '')) : NaN;
    const normalize = (str) => str.replace(/[\s,._]/g, '').toLowerCase();

    let efficiencyClassAndMaterialMatch = true;

    if (filterInfo.filterType !== 'VCP' && filterInfo.filterEfficiency) {
        efficiencyClassAndMaterialMatch =
            !isNaN(efficiency) &&
            efficiency === filterInfo.filterEfficiency &&
            normalize(filter.product.filterClass) === normalize(filterInfo.filterClass) &&
            filter.product.material === filterInfo.material;
    }

    return (
        efficiencyClassAndMaterialMatch &&
        (filterInfo.filterType !== 'VPF' || filter.product.typeFromName === 'VPF' || filter.product.typeFromName === 'PF') &&
        filter.product.dimensionHeightMm === filterInfo.dimensionHeight &&
        filter.product.dimensionWidthMm === filterInfo.dimensionWidth &&
        filter.product.dimensionDepthMm <= filterInfo.maxDepth
    );
};

export const newProductSelected = async (
    selectedProduct,
    filterProducts,
    machine,
    machineChangedCb,
    getFilterDiscountCb,
    getFilterPriceCb,
    productBundles,
    contractId
) => {
    const newMachine = new Machine(machine);

    for (let f of newMachine.filters) {
        if (!selectedProduct.bundle && f.product && f.product.id === selectedProduct.id) {
            f.count += 1;
            machineChangedCb(newMachine);
            return;
        } else if (selectedProduct.bundle && f.bundle && f.bundle.id === selectedProduct.id) {
            f.count += 1;
            machineChangedCb(newMachine);
            return;
        }
    }

    let discount = getFilterDiscountCb(selectedProduct);
    let price = getFilterPriceCb(selectedProduct);
    const objid = await ObjectIdGenerator.newId();
    const filter = new Filter({
        id: objid,
        product: !selectedProduct.bundle ? filterProducts.find((p) => p.id === selectedProduct.id) : null,
        bundle: selectedProduct.bundle ? productBundles.find((b) => b.id === selectedProduct.id) : null,
        viewOrder: newMachine.filters.length + 1,
        count: 1,
    });
    const pricingInfo = selectedProduct.bundle
        ? await getBundlePricingData(contractId ? contractId : 0, filter.bundle.clusterId)
        : await getProductPricingData(contractId ? contractId : 0, filter.product.storageId);

    if (filter.product && newMachine.filterInfos && newMachine.filterInfos.length > 0) {
        const filterInfo = newMachine.filterInfos.find((info) => filterMatchesFilterInfo(filter, info));
        if (filterInfo && filterInfo.count) {
            filter.count = filterInfo.count;
        }
    }

    filter.contractPricesInUse = pricingInfo[0].contractPricesInUse;
    filter.unitContractNetPrice = pricingInfo[0].discountedPrice;
    filter.discount = discount === null && price === null ? 0 : discount;
    filter.price = price;
    newMachine.filters.push(filter);
    machineChangedCb(newMachine);
};

export default function LocationEditMachine(props) {
    const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);
    const [showAddFilterSearch, setShowAddFilterSearch] = useState(false);
    const [expand, setExpand] = useState(props.expand);
    const [openingNewFilterDialog, setOpeningNewFilterDialog] = useState(false);
    const [showReplaceFilterSearch, setShowReplaceFilterSearch] = useState(false);
    const [machineAirVolume, setMachineAirVolume] = useState(props.machine.airVolume);
    const [toReplaceFilter, setToReplaceFilter] = useState(null);
    const [productRequestHint, setProductRequestHint] = useState('');
    const [selectedFilterInfo, setSelectedFilterInfo] = useState([]);
    const [showSurveyMachineInfo, setShowSurveyMachineInfo] = useState(false);
    const [selectedSuggestedCombinationId, setSelectedSuggestedCombinationId] = useState('');
    const [suggestedCombinationSearch, setSuggestedCombinationSearch] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    const prevPropsExpand = React.useRef(props.expand);
    const prevMachineIdRef = React.useRef(null);
    const { t } = useTranslation();
    const machineId = props.machine ? props.machine.id : null;
    const machine = props.machine;
    const suggestedFilterCombinations = machine.suggestedFilterCombinations || [];

    useEffect(() => {
        if (prevPropsExpand.current !== props.expand) {
            setExpand(props.expand);
            prevPropsExpand.current = props.expand;
        }
    }, [props.expand, expand]);

    useEffect(() => {
        if (props.machineFromSurvey && props.machine) {
            const airvFromSurvey = parseFloat(props.machineFromSurvey.airVolume);
            const airvMachine = parseFloat(props.machine.airVolume);
            if (
                (props.machineFromSurvey.name || '') !== (props.machine.name || '') ||
                (props.machineFromSurvey.room || '') !== (props.machine.machineLocation || '') ||
                (props.machineFromSurvey.serviceArea || '') !== (props.machine.machineArea || '') ||
                Math.abs(airvFromSurvey - airvMachine) > 0
            ) {
                setShowSurveyMachineInfo(true);
            } else {
                setShowSurveyMachineInfo(false);
            }
        }
    }, [props.machineFromSurvey, props.machine]);

    useEffect(() => {
        const suggestedCombinations =
            props.machine && props.machine.suggestedFilterCombinations
                ? props.machine.suggestedFilterCombinations
                : [];

        console.log('[LocationEditMachine] suggested combinations', {
            machineId: props.machine ? props.machine.id : null,
            machineName: props.machine ? props.machine.name : null,
            suggestedCombinationCount: suggestedCombinations.length,
            suggestedCombinations,
        });
    }, [props.machine]);

    useEffect(() => {
        const machineChanged = prevMachineIdRef.current !== machineId;

        if (!suggestedFilterCombinations.length) {
            setShowSuggestions(false);
            setSelectedSuggestedCombinationId('');
            if (machineChanged) {
                setSuggestedCombinationSearch('');
            }
            prevMachineIdRef.current = machineId;
            return;
        }

        setSelectedSuggestedCombinationId((currentId) => {
            if (currentId && suggestedFilterCombinations.some((c) => c.combinationId === currentId)) {
                return currentId;
            }
            return suggestedFilterCombinations[0].combinationId;
        });

        if (machineChanged) {
            setShowSuggestions(true);
            setSuggestedCombinationSearch('');
        }

        prevMachineIdRef.current = machineId;
    }, [machineId, suggestedFilterCombinations]);

    const handleChangeAirVolume = (value) => {
        const newValue = value.toString().replace(',', '.');
        if (newValue !== '' && !/^\d+[.]?\d{0,4}$/.test(newValue)) {
            return;
        }
        setMachineAirVolume(newValue);
    };

    const handleChange = (event) => {
        const target = event.target;
        const value = target.value;
        const name = target.name;
        switch (name) {
            case 'airvolume': {
                let newMachine = new Machine(props.machine);
                let triggerUpdate = false;
                if (machineAirVolume === '') {
                    newMachine.airVolume = 0;
                    triggerUpdate = true;
                } else if (newMachine.airVolume !== machineAirVolume) {
                    newMachine.airVolume = machineAirVolume;
                    triggerUpdate = true;
                }
                if (triggerUpdate) {
                    props.machineChanged(newMachine);
                }

                break;
            }
            case 'machineLocation': {
                let newMachine = new Machine(props.machine);
                newMachine.machineLocation = value;
                props.machineChanged(newMachine);
                break;
            }
            case 'machineArea': {
                let newMachine = new Machine(props.machine);
                newMachine.machineArea = value;
                props.machineChanged(newMachine);
                break;
            }
            case 'additionalInfo': {
                let newMachine = new Machine(props.machine);
                newMachine.additionalInfo = value;
                props.machineChanged(newMachine);
                break;
            }
            case 'machineName': {
                let newMachine = new Machine(props.machine);
                newMachine.name = value;
                props.machineChanged(newMachine);
                break;
            }
            default:
                break;
        }
    };

    const filterCountChanged = (id, value) => {
        let newMachine = new Machine(props.machine);
        for (let filt of newMachine.filters) {
            if (filt.id === id) {
                if (value === 0) {
                    newMachine.filters.splice(newMachine.filters.indexOf(filt), 1);
                    fixViewOrdering(newMachine);
                } else {
                    filt.count = value;
                }
                props.machineChanged(newMachine);
            }
        }
    };

    const removeFilter = (id) => {
        let newMachine = new Machine(props.machine);
        for (let filt of newMachine.filters) {
            if (filt.id === id) {
                newMachine.filters.splice(newMachine.filters.indexOf(filt), 1);
                fixViewOrdering(newMachine);
                props.machineChanged(newMachine);
            }
        }
    };

    const fixViewOrdering = (machine) => {
        // Assuming filters are always in correct order in machine object
        let ind = 1;
        for (const f of machine.filters) {
            f.viewOrder = ind++;
        }
    };

    const onClickDeleteMachine = (e) => {
        setConfirmDeleteModalOpen(true);
    };

    const confirmDeleteMachine = () => {
        props.machineRemoved(props.machine);
        setConfirmDeleteModalOpen(false);
    };

    const cancelDeleteMachine = () => {
        setConfirmDeleteModalOpen(false);
    };

    const onClickNewFilter = async (filterInfo) => {
        setOpeningNewFilterDialog(true);
        if (filterInfo) {
            setSelectedFilterInfo([filterInfo]);
        } else {
            setSelectedFilterInfo([]);
        }
        setShowAddFilterSearch(true);
        setOpeningNewFilterDialog(false);
    };

    const newFilterProductSelected = async (id, item) => {
        setShowAddFilterSearch(false);
        if (item) {
            await newProductSelected(
                item,
                props.filterProducts,
                props.machine,
                props.machineChanged,
                props.getFilterDiscount,
                props.getFilterPrice,
                props.productBundles,
                props.contractId
            );
        }
    };

    const handleDiscountChange = (id, e) => {
        let value = e.target.value.toString().replace(',', '.');
        let newMachine = new Machine(props.machine);
        for (let filt of newMachine.filters) {
            if (filt.id === id) {
                if (!/^-?\d*[.]?\d{0,4}$/.test(value)) {
                    return;
                } else if (!(value > 100)) {
                    props.setFilterDiscount(filt.id, value);
                }
            }
        }
    };

    const handlePriceChange = (id, e) => {
        let value = e.target.value.toString().replace(',', '.');
        let newMachine = new Machine(props.machine);
        for (let filt of newMachine.filters) {
            if (filt.id === id) {
                if (!/^\d*[.]?\d{0,2}$/.test(value)) {
                    return;
                } else if (value >= 0) {
                    props.setFilterPrice(filt.id, value);
                }
            }
        }
    };

    const handleFilterAdditionalInfoChange = (id, e) => {
        let value = e.target.value;
        let newMachine = new Machine(props.machine);
        for (let filt of newMachine.filters) {
            if (filt.id === id) {
                filt.additionalInfo = value;
                props.machineChanged(newMachine);
            }
        }
    };

    const sanitizeDiscountValue = (id) => {
        let newMachine = new Machine(props.machine);
        for (let filt of newMachine.filters) {
            if (filt.id === id) {
                props.setFilterDiscount(filt.id, parseDecimal(filt.discount));
            }
        }
    };

    const sanitizePriceValue = (id) => {
        let newMachine = new Machine(props.machine);
        for (let filt of newMachine.filters) {
            if (filt.id === id) {
                if (filt.price === '') {
                    props.setFilterPrice(filt.id, 0);
                }
            }
        }
    };

    /*const sanitizeAirVolumeValue = () => {
        let newMachine = new Machine(props.machine);
        if (props.machine.airVolume === '') {
            newMachine.airVolume = 0;
            props.machineChanged(newMachine);
        }
    };*/

    const toggleExpand = () => {
        setExpand(!expand);
    };

    const onClickSortUp = () => {
        props.sortUp(props.machine.id);
    };

    const onClickSortDown = () => {
        props.sortDown(props.machine.id);
    };

    if ((machine.name == null || machine.name.length === 0) && !expand) setExpand(true);

    const materialSpeed = () => {
        return CalculateMaterialSpeed(machine.airVolume, machine.filters);
    };

    const getCombinationLabel = (combination) => {
        const speedText =
            combination && combination.materialSpeed != null
                ? `${Number(combination.materialSpeed).toFixed(3)} m/s`
                : '-';
        const productNames =
            combination && combination.items && combination.items.length > 0
                ? combination.items.map((item) => item.productName).filter((name) => !!name).join(' + ')
                : '';
        return productNames ? `${speedText} - ${productNames}` : speedText;
    };

    const getSelectedSuggestedCombination = () => {
        if (!suggestedFilterCombinations.length) {
            return null;
        }

        return (
            suggestedFilterCombinations.find((c) => c.combinationId === selectedSuggestedCombinationId) ||
            suggestedFilterCombinations[0]
        );
    };

    const showSuggestedProducts = () => {
        if (!suggestedFilterCombinations.length) {
            return;
        }

        setShowSuggestions(true);
        setSuggestedCombinationSearch('');
        setSelectedSuggestedCombinationId(
            selectedSuggestedCombinationId || suggestedFilterCombinations[0].combinationId
        );
    };

    const hideSuggestedProducts = () => {
        setShowSuggestions(false);
    };

    const selectSuggestedProducts = () => {
        const selectedCombination = getSelectedSuggestedCombination();
        if (!selectedCombination || !selectedCombination.items || selectedCombination.items.length === 0) {
            return;
        }

        const newMachine = new Machine(props.machine);
        const existingFiltersByProductId = new Map();
        for (const existingFilter of newMachine.filters) {
            if (existingFilter.product && !existingFilter.bundle) {
                existingFiltersByProductId.set(existingFilter.product.id, existingFilter);
            }
        }

        for (const item of selectedCombination.items) {
            const existingFilter = existingFiltersByProductId.get(item.productId);
            if (existingFilter) {
                existingFilter.count = item.count;
                continue;
            }

            const product = props.filterProducts.find((p) => p.id === item.productId);
            if (!product) {
                continue;
            }

            newMachine.filters.push(
                new Filter({
                    id: Math.random(),
                    product,
                    viewOrder: newMachine.filters.length + 1,
                    count: item.count,
                    discount: 0,
                    price: null,
                })
            );
        }

        fixViewOrdering(newMachine);
        props.machineChanged(newMachine);
    };

    const sortUp = (id) => {
        const newMachine = new Machine(props.machine);
        for (const f of newMachine.filters) {
            if (f.id === id) {
                if (f.viewOrder === 1) return;
                else {
                    const prev = newMachine.filters[f.viewOrder - 2];
                    prev.viewOrder++;
                    f.viewOrder--;
                    props.machineChanged(newMachine);
                }
            }
        }
    };

    const onDiscountPriceSwap = (id) => {
        props.swapDiscountPrice(id);
    };

    const replaceFilterProductWithNewProduct = async (newProductId, replaceType) => {
        if (newProductId) {
            await props.replaceProduct(
                toReplaceFilter.product ? toReplaceFilter.product.id : toReplaceFilter.bundle.id,
                newProductId,
                toReplaceFilter.id,
                machine.id,
                replaceType
            );
        }
        setShowReplaceFilterSearch(false);
        setToReplaceFilter(null);
    };

    const filterInfoHasEquivalentFilter = (filterInfo) => {
        if (!machine || !machine.filters || machine.filters.length === 0) {
            return false;
        }
        for (let filter of machine.filters) {
            if (filterMatchesFilterInfo(filter, filterInfo)) {
                return true;
            }
        }
        return false;
    };

    const getFilterInfoForFilter = (filter) => {
        if (!machine || !machine.filters || machine.filters.length === 0) {
            return null;
        }
        if (!machine.filterInfos || !machine.filterInfos.length) return null;

        for (const finfo of machine.filterInfos) {
            if (filterMatchesFilterInfo(filter, finfo)) {
                return finfo;
            }
        }
    };

    const acceptMachineChangesFromSurvey = (evt) => {
        evt.stopPropagation();
        let newMachine = new Machine(props.machine);
        newMachine.name = props.machineFromSurvey.name;
        newMachine.machineLocation = props.machineFromSurvey.room;
        newMachine.machineArea = props.machineFromSurvey.serviceArea;
        newMachine.airVolume = props.machineFromSurvey.airVolume;
        props.machineChanged(newMachine);
        setMachineAirVolume(props.machineFromSurvey.airVolume);
    };

    return (
        <Grid key={machine.id}>
            <Accordion expanded={expand}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} onClick={toggleExpand} name="expand-machine">
                    <Grid container>
                        <Grid item xs={12}>
                            <Box component="h5" sx={styles.machineName}>
                                <Box
                                    component="span"
                                    sx={
                                        showSurveyMachineInfo && props.machineFromSurvey
                                            ? styles.containsChangesFromSurvey
                                            : {}
                                    }>
                                    {machine.name}
                                    {machine.containsRemovedProducts() ? (
                                        <Tooltip title={t('location.hints.containsExpiredProducts')}>
                                            <WarningIcon sx={styles.warning} name="warningIcon-machine-expired" />
                                        </Tooltip>
                                    ) : machine.containsDraftProducts() ? (
                                        <Tooltip title={t('location.hints.containsDraftProducts')}>
                                            <WarningIcon sx={styles.warning} name="warningIcon-machine-draft" />
                                        </Tooltip>
                                    ) : null}
                                    &nbsp;{machine.machineLocation}&nbsp;{machine.machineArea}
                                </Box>
                                &nbsp;
                                {showSurveyMachineInfo && props.machineFromSurvey && (
                                    <Box component="span" sx={styles.highlight}>
                                        {props.machineFromSurvey.name} {props.machineFromSurvey.room}{' '}
                                        {props.machineFromSurvey.serviceArea}, {props.machineFromSurvey.airVolume} m³/s
                                        &nbsp;
                                        <IconButton
                                            name="update-machine-from-survey"
                                            onClick={acceptMachineChangesFromSurvey}>
                                            <CheckCircleIcon />
                                        </IconButton>
                                    </Box>
                                )}
                            </Box>
                        </Grid>
                    </Grid>
                </AccordionSummary>
                <AccordionDetails>
                    {expand && (
                        <div>
                            <Grid container spacing={2} justifyContent={'space-between'}>
                                <Grid item>
                                    <TextField
                                        label={t('location.machineName')}
                                        defaultValue={machine.name}
                                        onBlur={handleChange}
                                        name="machineName"
                                    />
                                </Grid>
                                <Grid item>
                                    <Button onClick={onClickSortUp} name="btn-machine-sort-up">
                                        <i className="fas fa-arrow-up fa-lg"></i>
                                    </Button>
                                    <Button onClick={onClickSortDown} name="btn-machine-sort-down">
                                        <i className="fas fa-arrow-down fa-lg"></i>
                                    </Button>
                                    <Button onClick={onClickDeleteMachine} name="btn-remove-machine">
                                        <i
                                            className="fas fa-trash-alt fa-lg"
                                            style={{ color: 'red' }}
                                            title={t('location.removeMachine')}
                                        />
                                    </Button>
                                </Grid>
                            </Grid>

                            <Grid container spacing={2}>
                                <Grid item xs={6} sm={3}>
                                    <TextField
                                        label={t('location.airVolume') + ' m³/s'}
                                        onChange={(e) => handleChangeAirVolume(e.target.value)}
                                        name="airvolume"
                                        value={machineAirVolume}
                                        onBlur={handleChange}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <TextField
                                        label={t('location.position')}
                                        onBlur={handleChange}
                                        name="machineLocation"
                                        defaultValue={machine.machineLocation}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <TextField
                                        label={t('location.serviceArea')}
                                        onBlur={handleChange}
                                        name="machineArea"
                                        defaultValue={machine.machineArea}
                                    />
                                </Grid>
                                <Grid item xs={6} sm={3}>
                                    <TextField
                                        label={t('general.additionalInformation')}
                                        onBlur={handleChange}
                                        name="additionalInfo"
                                        defaultValue={machine.additionalInfo}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        inputProps={{ readOnly: true }}
                                        label={t('location.materialSpeed') + ' m/s'}
                                        name="materialSpeed"
                                        value={materialSpeed()}
                                    />
                                </Grid>
                            </Grid>
                            <br />
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Table size="small" sx={styles.table}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>{t('general.product')}</TableCell>
                                                <TableCell>{t('pcs')}</TableCell>
                                                {!props.showcaseMode && (
                                                    <TableCell>
                                                        {t('general.discount')} / {t('general.price')}
                                                    </TableCell>
                                                )}
                                                <TableCell>{t('general.additionalInformation')}</TableCell>
                                                <TableCell></TableCell>
                                                <TableCell></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {machine.filters.map((filter) => (
                                                <TableRow key={filter.id}>
                                                    <TableCell>
                                                        <FilterProductWarning
                                                            product={filter.product}
                                                            bundle={filter.bundle}
                                                            locContractPriceInUse={props.contractId > 1}
                                                            contractPriceInUse={filter.contractPricesInUse}
                                                        />
                                                        {filter.product && (
                                                            <React.Fragment>
                                                                <span>{filter.product.productName}</span>
                                                                <br />
                                                                {filter.product.productCode !== '0' && (
                                                                    <span>{filter.product.productCode}</span>
                                                                )}
                                                                <br />
                                                                {t('general.surfaceArea')}:&nbsp;
                                                                {filter.product.fabricSurfaceArea && (
                                                                    <>{filter.product.fabricSurfaceArea} &#x33A1;</>
                                                                )}
                                                                <br />
                                                            </React.Fragment>
                                                        )}
                                                        {filter.bundle && (
                                                            <React.Fragment>
                                                                <span>{filter.bundle.name}</span> ({filter.bundle.code})
                                                                <br />
                                                                {filter.bundle.products.map((p) => (
                                                                    <Box sx={styles.bundleproduct}>
                                                                        {p.productCount} x {p.product.productName}
                                                                    </Box>
                                                                ))}
                                                                {t('general.surfaceArea')}:&nbsp;
                                                                {filter.fabricSurfaceArea()} &#x33A1;
                                                                <br />
                                                            </React.Fragment>
                                                        )}
                                                        {!props.showcaseMode && (
                                                            <React.Fragment>
                                                                {t('general.rrp')}{' '}
                                                                {formatCurrency(
                                                                    filter.catalogValuePerPcs(props.exchangeRate),
                                                                    2,
                                                                    props.currency
                                                                )}
                                                                /{t('pcs')}
                                                                {props.currency && props.currency !== 'EUR' && (
                                                                    <>
                                                                        {' '}
                                                                        ({formatCurrency(filter.catalogValuePerPcs(), 2, 'EUR')}/
                                                                        {t('pcs')})
                                                                    </>
                                                                )}
                                                                <br />
                                                                <FilterMargin
                                                                    filter={filter}
                                                                    currency={props.currency}
                                                                    exchangeRate={props.exchangeRate}
                                                                />
                                                            </React.Fragment>
                                                        )}

                                                        {/* Show survey information for all filters */}
                                                        {!props.fromSurvey && props.showSurveyInfos && (
                                                            <React.Fragment>
                                                                <SurveyFilterInfo
                                                                    filterInfo={getFilterInfoForFilter(filter)}
                                                                    id={machine.id + '-' + filter.id}
                                                                />{' '}
                                                            </React.Fragment>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <NumericInputElement
                                                            id={filter.id}
                                                            value={filter.count}
                                                            onChange={filterCountChanged}
                                                            type={TypeOnChange}
                                                            min={1}
                                                        />
                                                    </TableCell>
                                                    {!props.showcaseMode && (
                                                        <TableCell>
                                                            {filter.discount !== null &&
                                                                !filter.contractPricesInUse && (
                                                                    <Grid container>
                                                                        <Grid item xs={12} md={5}>
                                                                            <TextField
                                                                                name="filterDiscount"
                                                                                value={filter.cpNotInUseDiscountRounded()}
                                                                                onChange={(e) =>
                                                                                    handleDiscountChange(filter.id, e)
                                                                                }
                                                                                onBlur={() =>
                                                                                    sanitizeDiscountValue(filter.id)
                                                                                }
                                                                                InputProps={{
                                                                                    endAdornment: (
                                                                                        <InputAdornment position="end">
                                                                                            %
                                                                                        </InputAdornment>
                                                                                    ),
                                                                                }}
                                                                            />
                                                                        </Grid>
                                                                        <Grid
                                                                            item
                                                                            xs={12}
                                                                            md={2}
                                                                            sx={styles.swapIconCol}>
                                                                            <SwapHorizIcon
                                                                                name="swapIcon"
                                                                                onClick={() =>
                                                                                    onDiscountPriceSwap(filter.id)
                                                                                }
                                                                            />
                                                                        </Grid>
                                                                        <Grid item xs={12} md={5}>
                                                                            <span>
                                                                                {formatCurrency(
                                                                                    filter.cpNotInUseCalculateDiscountPricePerPcs(
                                                                                        props.exchangeRate
                                                                                    ),
                                                                                    2,
                                                                                    props.currency
                                                                                )}
                                                                                /{t('pcs')}
                                                                            </span>
                                                                        </Grid>
                                                                    </Grid>
                                                                )}
                                                            {filter.price !== null && !filter.contractPricesInUse && (
                                                                <Grid container>
                                                                    <Grid item xs={12} md={5}>
                                                                        <span>
                                                                            {filter.cpNotInUseCalculateDiscountPercent(
                                                                                props.exchangeRate
                                                                            )}{' '}
                                                                            %
                                                                        </span>
                                                                    </Grid>
                                                                    <Grid
                                                                        item
                                                                        xs={12}
                                                                        md={2}
                                                                        sx={styles.swapIconCol}>
                                                                        <SwapHorizIcon
                                                                            name="sawpIcon"
                                                                            onClick={() =>
                                                                                onDiscountPriceSwap(filter.id)
                                                                            }
                                                                        />
                                                                    </Grid>
                                                                    <Grid item xs={12} md={5}>
                                                                        <TextField
                                                                            name="filterPrice"
                                                                            value={filter.cpNotInUsePriceRounded()}
                                                                            onChange={(e) =>
                                                                                handlePriceChange(filter.id, e)
                                                                            }
                                                                            onBlur={() => sanitizePriceValue(filter.id)}
                                                                            InputProps={{
                                                                                endAdornment: (
                                                                                    <InputAdornment position="end">
                                                                                        {currencySymbolFor(props.currency)}
                                                                                        /{t('pcs')}
                                                                                    </InputAdornment>
                                                                                ),
                                                                            }}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                            )}
                                                            {filter.contractPricesInUse && (
                                                                <Grid container>
                                                                    <Grid item xs={12}>
                                                                        <Tooltip
                                                                            title={t(
                                                                                'location.contractPriceSetCannotEditHint'
                                                                            )}>
                                                                            <span name="contract-price">
                                                                                {t('general.contractPrice')}&nbsp;&nbsp;{' '}
                                                                                {formatCurrency(
                                                                                    filter.unitContractNetPrice,
                                                                                    2,
                                                                                    props.currency
                                                                                )}
                                                                                /{t('pcs')}
                                                                            </span>
                                                                        </Tooltip>
                                                                    </Grid>
                                                                </Grid>
                                                            )}
                                                        </TableCell>
                                                    )}
                                                    <TableCell>
                                                        <TextField
                                                            name="filteradditionalinfo"
                                                            value={filter.additionalInfo}
                                                            onChange={(e) =>
                                                                handleFilterAdditionalInfoChange(filter.id, e)
                                                            }
                                                        />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Button
                                                            onClick={() => sortUp(filter.id)}
                                                            name="btn-filter-sort-up">
                                                            <i className="fas fa-arrow-up fa-lg"></i>
                                                        </Button>
                                                    </TableCell>
                                                    <TableCell>
                                                        <IconButton
                                                            aria-label="delete-filter"
                                                            name="delete-filter"
                                                            onClick={() => removeFilter(filter.id)}>
                                                            <DeleteIcon />
                                                        </IconButton>

                                                        <Button
                                                            name="btn-change-product"
                                                            variant="contained"
                                                            color="primary"
                                                            onClick={() => {
                                                                setToReplaceFilter(filter);
                                                                setProductRequestHint(
                                                                    filter.product
                                                                        ? filter.product.productName
                                                                        : filter.bundle.name
                                                                );
                                                                setShowReplaceFilterSearch(true);
                                                            }}
                                                            disabled={false}
                                                            startIcon={<SwapHorizIcon />}>
                                                            {t('buttons.switch')}
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </Grid>
                                <br />
                            </Grid>

                            {suggestedFilterCombinations.length > 0 && !showAddFilterSearch && showSuggestions && (
                                <Grid
                                    container
                                    spacing={2}
                                    sx={{ marginTop: 1, marginBottom: 2 }}
                                    name="suggested-products-inline-panel">
                                    <Grid item xs={12}>
                                        <Box sx={{ fontWeight: 600 }}>{t('location.suggestedProductsDialogTitle')}</Box>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Autocomplete
                                            options={suggestedFilterCombinations}
                                            value={
                                                suggestedFilterCombinations.find(
                                                    (c) => c.combinationId === selectedSuggestedCombinationId
                                                ) || null
                                            }
                                            inputValue={suggestedCombinationSearch}
                                            onInputChange={(event, newInputValue) =>
                                                setSuggestedCombinationSearch(newInputValue)
                                            }
                                            onChange={(event, selectedOption) =>
                                                setSelectedSuggestedCombinationId(
                                                    selectedOption ? selectedOption.combinationId : ''
                                                )
                                            }
                                            getOptionLabel={(option) => getCombinationLabel(option)}
                                            isOptionEqualToValue={(option, value) =>
                                                option.combinationId === value.combinationId
                                            }
                                            filterOptions={(options, state) => {
                                                const phrase = state.inputValue.toLowerCase().trim();

                                                if (!phrase.length) {
                                                    return options;
                                                }

                                                return options.filter((option) =>
                                                    getCombinationLabel(option).toLowerCase().includes(phrase)
                                                );
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    name="suggested-combination-select"
                                                    label={t('location.suggestedCombinationSearch')}
                                                    variant="outlined"
                                                />
                                            )}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        {((getSelectedSuggestedCombination() && getSelectedSuggestedCombination().items) || []).map(
                                            (item, index) => (
                                                <div key={`suggested-combination-item-${index}`}>
                                                    {item.productName} ({item.count} {t('pcs')})
                                                </div>
                                            )
                                        )}
                                    </Grid>
                                    <Grid item>
                                        <Button
                                            name="btn-select-suggested-products"
                                            variant="contained"
                                            color="primary"
                                            onClick={selectSuggestedProducts}
                                            startIcon={<Add />}>
                                            {t('buttons.selectProducts')}
                                        </Button>
                                    </Grid>
                                    <Grid item>
                                        <Button
                                            name="btn-hide-suggested-products"
                                            variant="text"
                                            color="primary"
                                            onClick={hideSuggestedProducts}>
                                            {t('location.buttons.hideSuggestions')}
                                        </Button>
                                    </Grid>
                                </Grid>
                            )}

                            {suggestedFilterCombinations.length > 0 &&
                                !showAddFilterSearch &&
                                !showSuggestions && (
                                    <Grid container spacing={2} sx={{ marginTop: 1, marginBottom: 2 }}>
                                        <Grid item>
                                            <Button
                                                name="btn-show-suggested-products"
                                                variant="contained"
                                                color="primary"
                                                disabled={openingNewFilterDialog}
                                                startIcon={<Add />}
                                                onClick={showSuggestedProducts}>
                                                {t('location.buttons.showSuggestions')}
                                            </Button>
                                        </Grid>
                                    </Grid>
                                )}

                            {!showAddFilterSearch &&
                                (suggestedFilterCombinations.length === 0 || !showSuggestions) &&
                                (() => {
                                    /* Show survey filter add info in case we're creating new location or user wants to see survey infos */
                                    if (
                                        (props.fromSurvey || props.showSurveyInfos) &&
                                        machine.filterInfos &&
                                        machine.filterInfos.length > 0
                                    ) {
                                        const filterInfosWithEquivalent = machine.filterInfos.map((f) => ({
                                            filterInfo: f,
                                            hasEquivalent: filterInfoHasEquivalentFilter(f),
                                        }));
                                        const hasNonEquivalentFilter = filterInfosWithEquivalent.some(
                                            (f) => !f.hasEquivalent
                                        );
                                        if (hasNonEquivalentFilter) {
                                            return filterInfosWithEquivalent
                                                .filter((f) => !f.hasEquivalent)
                                                .map(({ filterInfo }, index) => (
                                                    <Grid
                                                        container
                                                        key={'survey-filter-info-' + index}
                                                        alignItems="center">
                                                        <Grid item>
                                                            <Button
                                                                key={'btn-add-new-product-' + index}
                                                                name="btn-add-new-product"
                                                                variant="contained"
                                                                color="primary"
                                                                disabled={openingNewFilterDialog}
                                                                startIcon={<Add />}
                                                                onClick={() => onClickNewFilter(filterInfo)}>
                                                                {t('general.product')}
                                                            </Button>
                                                        </Grid>
                                                        <Grid item>
                                                            <SurveyFilterInfo
                                                                filterInfo={filterInfo}
                                                                id={machine.id + '-' + index}
                                                            />
                                                        </Grid>
                                                    </Grid>
                                                ));
                                        }
                                    }
                                    return (
                                        <Grid container>
                                            <Grid item>
                                                <Button
                                                    name="btn-add-new-product"
                                                    variant="contained"
                                                    color="primary"
                                                    disabled={openingNewFilterDialog}
                                                    startIcon={<Add />}
                                                    onClick={() => onClickNewFilter(undefined)}>
                                                    {t('general.product')}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    );
                                })()}

                            {showAddFilterSearch && (
                                <FilterProductSelectAutoComplete
                                    filterInfos={selectedFilterInfo}
                                    showMaterialSpeed={props.fromSurvey || props.showSurveyInfos}
                                    selected={newFilterProductSelected}
                                    show={showAddFilterSearch}
                                    allowNewProd={true}
                                    showBundles={true}
                                />
                            )}

                            {showReplaceFilterSearch && (
                                <FilterProductChangeDialog
                                    selected={replaceFilterProductWithNewProduct}
                                    show={showReplaceFilterSearch}
                                    allowNewProd={true}
                                    productRequestHint={productRequestHint}
                                />
                            )}
                            <br />
                        </div>
                    )}
                </AccordionDetails>
            </Accordion>

            <ConfirmationDialog
                open={confirmDeleteModalOpen}
                confirm={confirmDeleteMachine}
                cancel={cancelDeleteMachine}
                confirmText={'Poista'}>
                <span>{t('location.confirmRemoveMachine', { machineName: machine.name })}</span>
            </ConfirmationDialog>
        </Grid>
    );
}
